import { useBrowserHistoryAndURL } from '@hooks/history/useBrowserHistoryAndURL';
import { InfinitePageType, PaginationPageType } from '@type/swr';
import { useBaseFiltersQueryParamsState } from './useBaseFiltersQueryParamsState';

interface UseFiltersQueryParamsProps<T extends InfinitePageType = 'page'> {
  serverQueryParams?: object,
  initialPage?: PaginationPageType<T>,
  keepAsCommaSeparatedValues?: string[],
  sortQueryParams?: boolean,
  paginationType?: T
}

/**
 * @description A hook to manage the query params for the filters (for Pages Router).
 * It leverages useBaseFiltersQueryParamsState for state management and adds
 * browser history updates via useBrowserHistoryAndURL.
 *
 * Params: (Referencing base hook params + specific ones)
 * - `serverQueryParams`: *object* - the query params from the server
 * - `initialPage`: *string | number* - the initial page
 * - `keepAsCommaSeparatedValues`: *string[]* - an array of keys that should be kept as comma separated values
 * - `sortQueryParams`: *boolean* - if true, the query params will be sorted alphabetically. Default true.
 * - `paginationType`: *InfinitePageType* - the type of pagination used.
 *
 * Returns:
 * - `queryParams`: *object* - the current query params
 * - `setQueryParams`: *function* - a function to update the query params
 * - `currentPage`: *string | number* - the current page
 * - `setCurrentPage`: *function* - a function to update the current page
 *
 * @example
 * const {
 *   queryParams,
 *   setQueryParams,
 *   setCurrentPage,
 * } = useFiltersQueryParams({
 *   serverQueryParams: objFilterParams, // objFilterParams is an object with the query params from the server
 *   initialPage: page, // page is a number, ie: 1
 *   keepAsCommaSeparatedValues: ['spokenLanguages'], // look at params description for more info
 * });
 *
 * // then use queryParams and setQueryParams in the filters hook in components (you should do some prop drilling)
 * const { formRef, handleStep, record, submitForm } = useFiltersHub({ queryParams, setQueryParams, externalFieldsList: ['keyword'] });
 */
export const useFiltersQueryParams = <T extends InfinitePageType = 'page'>(props: UseFiltersQueryParamsProps<T>) => {
  // Destructure props
  const {
    serverQueryParams,
    initialPage,
    keepAsCommaSeparatedValues,
    sortQueryParams = true,
    paginationType = 'page' as T,
  } = props;

  // Get state and setters from the base hook
  const {
    queryParams,
    setQueryParams,
    currentPage,
    setCurrentPage,
  } = useBaseFiltersQueryParamsState<T>({
    serverQueryParams,
    initialPage,
    keepAsCommaSeparatedValues,
    sortQueryParams,
    paginationType,
  });

  // History management hook call
  useBrowserHistoryAndURL(currentPage, queryParams, setQueryParams, paginationType);

  // Return the state and setters
  return {
    queryParams,
    setQueryParams,
    currentPage,
    setCurrentPage,
  };
};
